import { useRef, useState, type PointerEvent, type ReactNode } from "react";

const OPEN = -96;

export default function SwipeableRow({
  children,
  onDelete,
}: {
  children: ReactNode;
  onDelete: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; y: number; base: number } | null>(null);

  const onPointerDown = (e: PointerEvent) => {
    start.current = { x: e.clientX, y: e.clientY, base: offset };
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!start.current) return;
    const dx = e.clientX - start.current.x;
    const dy = e.clientY - start.current.y;
    if (!dragging) {
      // Only claim clearly-horizontal moves so vertical scrolling stays native.
      if (Math.abs(dx) < 10 || Math.abs(dx) < Math.abs(dy)) return;
      setDragging(true);
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Capture is best-effort; the row still swipes without it.
      }
    }
    setOffset(Math.min(0, Math.max(OPEN - 24, start.current.base + dx)));
  };

  const onPointerEnd = () => {
    if (!start.current) return;
    if (dragging) {
      setOffset((o) => (o < OPEN / 2 ? OPEN : 0));
    } else if (offset !== 0) {
      setOffset(0);
    }
    setDragging(false);
    start.current = null;
  };

  return (
    <div className="relative overflow-hidden rounded-[14px]">
      <button
        onClick={() => {
          setOffset(0);
          onDelete();
        }}
        tabIndex={offset === OPEN ? 0 : -1}
        style={{ visibility: offset === 0 ? "hidden" : "visible" }}
        className="absolute inset-y-0 right-0 flex w-[96px] items-center justify-center bg-danger font-bold text-white"
      >
        Delete
      </button>
      <div
        className="relative touch-pan-y"
        style={{
          transform: `translateX(${offset}px)`,
          transition: dragging ? "none" : "transform 0.2s ease-out",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        {children}
      </div>
    </div>
  );
}
